import { canonicalRollup, groupBoardLines } from './BoardPage';

export const ROLLUP_KPI_LABELS = {
  'Interest Income': 'NII (interest income)',
  'Interest Expense': 'NII (interest expense)',
  'Non-Interest Income': 'Total NIR',
  'Non-Interest Expense': 'Total NIE',
  Provision: 'Provision',
  Tax: 'Tax'
};

export const FAMILY_ROLLUPS = [
  'Non-Interest Expense',
  'Non-Interest Income',
  'Interest Income',
  'Interest Expense'
];

export const NM_REFERENCE_THRESHOLD = 1000;

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const lineKey = (line = {}) =>
  `${line.planningEntity || 'company'}::${line.glGroup || ''}`;

export const basisLabel = (basis) => {
  const labels = {
    budget: 'Budget',
    prior: 'Prior',
    prior_period: 'Prior period',
    prior_year: 'Prior year'
  };
  return labels[basis] || String(basis || 'Reference').replace(/_/g, ' ');
};

export const rollupHeadingLabel = (rollup) => {
  const canonical = canonicalRollup(rollup);
  if (canonical === 'Interest Income' || canonical === 'Interest Expense') return 'NII';
  if (canonical === 'Non-Interest Income') return 'NIR';
  if (canonical === 'Non-Interest Expense') return 'NIE';
  if (canonical === 'Balance Sheet') return 'Balance sheet';
  return canonical;
};

export const formatAccounting = (value, { decimals = 0 } = {}) => {
  const n = num(value);
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  return n < 0 ? `(${formatted})` : formatted;
};

export const formatDeltaPercent = (variance, reference) => {
  const ref = num(reference);
  if (Math.abs(ref) < NM_REFERENCE_THRESHOLD) return 'NM';
  const pct = (num(variance) / Math.abs(ref)) * 100;
  const rounded = Math.abs(pct) < 0.05 ? 0 : pct;
  if (rounded === 0) return '0.0%';
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded.toFixed(1)}%`;
};

export const sumLineVariances = (lines = []) =>
  lines.reduce((sum, line) => sum + num(line.variance), 0);

const signedPresentationValue = (line = {}, key) =>
  num(line[`${key}Impact`] ?? line[key]);

const sumLineActuals = (lines = []) =>
  lines.reduce((sum, line) => sum + signedPresentationValue(line, 'actual'), 0);

const sumLineReferences = (lines = []) =>
  lines.reduce((sum, line) => sum + signedPresentationValue(line, 'reference'), 0);

export const computeRollupSubtotals = (lines = []) => {
  const grouped = groupBoardLines(lines);
  return grouped.map((group) => ({
    rollup: group.rollup,
    label: ROLLUP_KPI_LABELS[group.rollup] || group.rollup,
    variance: sumLineVariances(group.rows),
    actual: sumLineActuals(group.rows),
    reference: sumLineReferences(group.rows)
  }));
};

export const computeExecutiveKpis = (lines = []) => {
  const subtotals = computeRollupSubtotals(lines);
  const byRollup = Object.fromEntries(
    subtotals.map((row) => [
      row.rollup,
      { variance: row.variance, actual: row.actual, reference: row.reference }
    ])
  );
  const incomeRollups = new Set([
    'Interest Income',
    'Interest Expense',
    'Non-Interest Income',
    'Non-Interest Expense',
    'Provision',
    'Tax'
  ]);
  const pretaxProxy = subtotals
    .filter((row) => incomeRollups.has(row.rollup))
    .reduce((sum, row) => sum + row.variance, 0);
  const pretaxActual = subtotals
    .filter((row) => incomeRollups.has(row.rollup))
    .reduce((sum, row) => sum + row.actual, 0);
  const pretaxReference = subtotals
    .filter((row) => incomeRollups.has(row.rollup))
    .reduce((sum, row) => sum + row.reference, 0);
  const bsSubtotals = subtotals.filter((row) => row.rollup === 'Balance Sheet');

  return {
    totalVariance: sumLineVariances(lines),
    totalActual: sumLineActuals(lines),
    totalReference: sumLineReferences(lines),
    niiIncome: byRollup['Interest Income']?.variance ?? 0,
    niiExpense: byRollup['Interest Expense']?.variance ?? 0,
    niiActual:
      (byRollup['Interest Income']?.actual ?? 0) + (byRollup['Interest Expense']?.actual ?? 0),
    niiReference:
      (byRollup['Interest Income']?.reference ?? 0) + (byRollup['Interest Expense']?.reference ?? 0),
    totalNir: byRollup['Non-Interest Income']?.variance ?? 0,
    nirActual: byRollup['Non-Interest Income']?.actual ?? 0,
    nirReference: byRollup['Non-Interest Income']?.reference ?? 0,
    totalNie: byRollup['Non-Interest Expense']?.variance ?? 0,
    nieActual: byRollup['Non-Interest Expense']?.actual ?? 0,
    nieReference: byRollup['Non-Interest Expense']?.reference ?? 0,
    provision: byRollup.Provision?.variance ?? 0,
    provisionActual: byRollup.Provision?.actual ?? 0,
    provisionReference: byRollup.Provision?.reference ?? 0,
    tax: byRollup.Tax?.variance ?? 0,
    taxActual: byRollup.Tax?.actual ?? 0,
    taxReference: byRollup.Tax?.reference ?? 0,
    pretaxProxy,
    pretaxActual,
    pretaxReference,
    bsVariance: bsSubtotals.reduce((sum, row) => sum + row.variance, 0),
    bsActual: bsSubtotals.reduce((sum, row) => sum + row.actual, 0),
    bsReference: bsSubtotals.reduce((sum, row) => sum + row.reference, 0),
    subtotals
  };
};

const hasMaterialValue = (...values) =>
  values.some((value) => Math.abs(num(value)) > 0.0001);

const pAndLRollups = [
  'Interest Income',
  'Interest Expense',
  'Non-Interest Income',
  'Non-Interest Expense',
  'Provision',
  'Tax'
];

const makeExecutiveRow = ({
  id,
  label,
  section = 'earnings',
  screen = null,
  rollups = [],
  match = null,
  actual,
  reference,
  variance
}) => ({
  id,
  label,
  section,
  screen,
  rollups,
  match,
  actual: num(actual),
  reference: num(reference),
  variance: num(variance),
  variancePct: formatDeltaPercent(variance, reference)
});

const slug = (value) =>
  String(value || 'row')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'row';

const lineText = (line = {}) =>
  `${line.glGroup || ''} ${line.planningEntity || ''} ${line.lob || ''}`.toLowerCase();

const isLoanLine = (line = {}) => /loan/.test(lineText(line));
const isDepositLine = (line = {}) => /deposit/.test(lineText(line));
const isSecurityLine = (line = {}) => /securit|investment/.test(lineText(line));
const isBorrowingLine = (line = {}) => /borrow|debt|fhlb|funding|repurchase/.test(lineText(line));
const isEquityLine = (line = {}) =>
  canonicalRollup(line.rollup) === 'Balance Sheet' &&
  (String(line.bsClass || '').toLowerCase() === 'equity' ||
    /equity|capital|stock|retained/.test(lineText(line)));

const sumMatchingLines = (lines = [], match) =>
  (lines || []).filter(match).reduce((totals, line) => ({
    actual: totals.actual + signedPresentationValue(line, 'actual'),
    reference: totals.reference + signedPresentationValue(line, 'reference'),
    variance: totals.variance + num(line.variance)
  }), { actual: 0, reference: 0, variance: 0 });

const isBalanceSheetLine = (line = {}) => canonicalRollup(line.rollup) === 'Balance Sheet';

const bsOtherMatch = (className) => (line) => {
  const bsClass = String(line.bsClass || '').toLowerCase();
  return isBalanceSheetLine(line) &&
    bsClass === className &&
    !isLoanLine(line) &&
    !isDepositLine(line) &&
    !isSecurityLine(line) &&
    !isBorrowingLine(line) &&
    !isEquityLine(line);
};

export const buildBalanceSheetSummaryRows = (lines = []) => {
  const rows = [
    {
      id: 'bs_loans',
      label: 'Loans',
      section: 'balance sheet highlight',
      screen: 'bs',
      rollups: ['Balance Sheet'],
      match: (line) => isBalanceSheetLine(line) && isLoanLine(line)
    },
    {
      id: 'bs_deposits',
      label: 'Deposits',
      section: 'balance sheet highlight',
      screen: 'bs',
      rollups: ['Balance Sheet'],
      match: (line) => isBalanceSheetLine(line) && isDepositLine(line)
    },
    {
      id: 'bs_securities',
      label: 'Investment securities',
      section: 'assets',
      screen: 'bs',
      rollups: ['Balance Sheet'],
      match: (line) => isBalanceSheetLine(line) && isSecurityLine(line)
    },
    {
      id: 'bs_borrowings',
      label: 'Borrowings and debt',
      section: 'liabilities',
      screen: 'bs',
      rollups: ['Balance Sheet'],
      match: (line) => isBalanceSheetLine(line) && isBorrowingLine(line)
    },
    {
      id: 'bs_other_assets',
      label: 'Other assets',
      section: 'assets',
      screen: 'bs',
      rollups: ['Balance Sheet'],
      match: bsOtherMatch('assets')
    },
    {
      id: 'bs_other_liabilities',
      label: 'Other liabilities',
      section: 'liabilities',
      screen: 'bs',
      rollups: ['Balance Sheet'],
      match: bsOtherMatch('liabilities')
    },
    {
      id: 'bs_equity',
      label: 'Equity',
      section: 'equity',
      screen: 'bs',
      rollups: ['Balance Sheet'],
      match: isEquityLine
    }
  ];

  return rows
    .map((row) => makeExecutiveRow({ ...row, ...sumMatchingLines(lines, row.match) }))
    .filter((row) => hasMaterialValue(row.actual, row.reference, row.variance));
};

const sumRows = (rows = []) => ({
  actual: sumLineActuals(rows),
  reference: sumLineReferences(rows),
  variance: sumLineVariances(rows)
});

// Per-screen summary rows for the four-box executive canvas. Rows are the major
// matrix groups for that screen — aggregated by GL group across business units, not
// every planning-entity GL line — so the matrix reads as a summary, and the row set is
// a clean single-level partition (matrix, waterfall, and drilldown stay in lock-step
// and never double-count a rollup with its own children).
export const buildScreenSummaryRows = (screenId, lines = []) => {
  if (screenId === 'exec') return buildExecutiveSummaryRows(lines);
  if (screenId === 'bs') return buildBalanceSheetSummaryRows(lines);

  const grouped = groupBoardLines(lines);
  const rows = [];
  grouped.forEach((group) => {
    const rollup = canonicalRollup(group.rollup);
    const byGlGroup = new Map();
    group.rows.forEach((line) => {
      const key = line.glGroup || 'Unmapped GL group';
      if (!byGlGroup.has(key)) byGlGroup.set(key, []);
      byGlGroup.get(key).push(line);
    });
    Array.from(byGlGroup.entries())
      .map(([glGroup, groupRows]) => ({ glGroup, totals: sumRows(groupRows) }))
      .sort((a, b) => Math.abs(b.totals.variance) - Math.abs(a.totals.variance))
      .forEach(({ glGroup, totals }) => {
        rows.push(makeExecutiveRow({
          id: `${screenId}_gl_${slug(rollup)}_${slug(glGroup)}`,
          label: glGroup,
          section: rollupHeadingLabel(rollup),
          screen: screenId,
          rollups: [rollup],
          match: (line) =>
            canonicalRollup(line?.rollup) === rollup &&
            String(line?.glGroup || '') === String(glGroup),
          ...totals
        }));
      });
  });

  return rows.filter((row) => hasMaterialValue(row.actual, row.reference, row.variance));
};

export const buildExecutiveSummaryRows = (lines = []) => {
  const kpis = computeExecutiveKpis(lines);
  const balanceSheetHighlights = buildBalanceSheetSummaryRows(lines)
    .filter((row) => row.id === 'bs_loans' || row.id === 'bs_deposits');
  const rows = [
    makeExecutiveRow({
      id: 'earnings',
      label: 'Earnings variance',
      section: 'summary',
      rollups: pAndLRollups,
      actual: kpis.pretaxActual,
      reference: kpis.pretaxReference,
      variance: kpis.pretaxProxy
    }),
    makeExecutiveRow({
      id: 'nii',
      label: 'Net interest income',
      screen: 'nim',
      rollups: ['Interest Income', 'Interest Expense'],
      actual: kpis.niiActual,
      reference: kpis.niiReference,
      variance: kpis.niiIncome + kpis.niiExpense
    }),
    makeExecutiveRow({
      id: 'nir',
      label: 'Total non-interest revenue',
      screen: 'nir',
      rollups: ['Non-Interest Income'],
      actual: kpis.nirActual,
      reference: kpis.nirReference,
      variance: kpis.totalNir
    }),
    makeExecutiveRow({
      id: 'nie',
      label: 'Total non-interest expense',
      screen: 'nie',
      rollups: ['Non-Interest Expense'],
      actual: kpis.nieActual,
      reference: kpis.nieReference,
      variance: kpis.totalNie
    }),
    makeExecutiveRow({
      id: 'provision',
      label: 'Provision for credit losses',
      screen: 'provision',
      rollups: ['Provision'],
      actual: kpis.provisionActual,
      reference: kpis.provisionReference,
      variance: kpis.provision
    }),
    makeExecutiveRow({
      id: 'tax',
      label: 'Tax expense',
      rollups: ['Tax'],
      actual: kpis.taxActual,
      reference: kpis.taxReference,
      variance: kpis.tax
    })
  ].concat(balanceSheetHighlights);

  return rows.filter((row) =>
    row.id === 'earnings' || hasMaterialValue(row.actual, row.reference, row.variance)
  );
};

export const synthesizeSummaryWaterfall = (rows = [], totalLabel = 'Total movement') => {
  const components = (rows || [])
    .filter((row) => row.id !== 'earnings' && hasMaterialValue(row.actual, row.reference, row.variance))
    .map((row) => ({ id: row.id, label: row.label, amount: row.variance }));
  return {
    components,
    total: components.reduce((sum, component) => sum + num(component.amount), 0),
    totalLabel
  };
};

export const synthesizeExecWaterfall = (kpis = {}) => {
  const components = [
    { id: 'nii', label: 'Net interest income', amount: (kpis.niiIncome ?? 0) + (kpis.niiExpense ?? 0) },
    { id: 'nir', label: 'Total NIR', amount: kpis.totalNir ?? 0 },
    { id: 'nie', label: 'Total NIE', amount: kpis.totalNie ?? 0 },
    { id: 'provision', label: 'Provision', amount: kpis.provision ?? 0 }
  ];
  if (kpis.tax) {
    components.push({ id: 'tax', label: 'Tax', amount: kpis.tax });
  }
  return {
    components,
    total: kpis.pretaxProxy ?? 0,
    totalLabel: 'Pretax proxy'
  };
};

const normalizeIdList = (value) =>
  Array.isArray(value) ? value.map((item) => String(item)).filter(Boolean) : [];

const readCitationList = (citations = {}, camelKey, snakeKey) =>
  normalizeIdList(citations?.[camelKey] ?? citations?.[snakeKey]);

const mergeCitationLists = (...citationObjs) => {
  const keys = [
    ['bridgeComponentIds', 'bridge_component_ids'],
    ['findingIds', 'finding_ids'],
    ['dispositionIds', 'disposition_ids'],
    ['commentIds', 'comment_ids'],
    ['expectationIds', 'expectation_ids'],
    ['forecastContextIds', 'forecast_context_ids'],
    ['forecastOverrideIds', 'forecast_override_ids']
  ];
  const out = {};
  keys.forEach(([camelKey, snakeKey]) => {
    const seen = new Set();
    citationObjs.forEach((citations) => {
      readCitationList(citations || {}, camelKey, snakeKey).forEach((id) => seen.add(id));
    });
    if (seen.size) out[camelKey] = Array.from(seen);
  });
  return out;
};

const normalizeFinding = (finding = {}) => ({
  ...finding,
  id: finding.id,
  planningEntity: finding.planningEntity || finding.planning_entity,
  glGroup: finding.glGroup || finding.gl_group,
  rollup: canonicalRollup(finding.rollup),
  score: num(finding.score),
  dollarImpact: num(finding.dollarImpact ?? finding.dollar_impact),
  status: finding.status,
  brief: finding.brief || '',
  disposition: finding.disposition || null,
  verdict: finding.verdict || finding.disposition?.verdict || null,
  dispositionNote: finding.dispositionNote || finding.disposition_note || finding.disposition?.note || ''
});

const normalizeFindings = (findingsById = {}) => {
  const rows = Array.isArray(findingsById)
    ? findingsById
    : Object.values(findingsById || {});
  return rows.filter(Boolean).map(normalizeFinding);
};

const rowMatchesLine = (row, line) =>
  typeof row.match === 'function'
    ? row.match(line)
    : row.rollups.includes(canonicalRollup(line?.rollup));

export const executiveDriverLines = (row, lines = [], limit = 4) =>
  [...(lines || [])]
    .filter((line) => rowMatchesLine(row, line))
    .sort((a, b) => Math.abs(num(b.variance)) - Math.abs(num(a.variance)))
    .slice(0, limit);

const findingMatchesLine = (finding, line) =>
  finding.planningEntity === line?.planningEntity && finding.glGroup === line?.glGroup;

const findingMatchesAnyLine = (finding, lines = []) =>
  lines.some((line) => findingMatchesLine(finding, line));

const bridgeIdsForLines = (evidence = {}, lines = []) => {
  const ids = [];
  Object.entries(evidence?.bridges || {}).forEach(([id, bridge]) => {
    const planningEntity = bridge.planningEntity || bridge.planning_entity;
    const glGroup = bridge.glGroup || bridge.gl_group;
    if (lines.some((line) => line.planningEntity === planningEntity && line.glGroup === glGroup)) {
      ids.push(id);
    }
  });
  return ids;
};

const dispositionIdsFromFindings = (findings = []) =>
  findings
    .map((finding) => finding.disposition?.id)
    .filter(Boolean);

const citationObj = (key, values) => {
  const clean = normalizeIdList(values);
  return clean.length ? { [key]: clean } : {};
};

const summarizeDrivers = (drivers = []) =>
  drivers
    .slice(0, 3)
    .map((line) => `${line.glGroup || 'line'} at ${line.planningEntity || 'company'} ${formatAccounting(line.variance)}`)
    .join('; ');

const cleanSentence = (value = '') =>
  String(value || '').replace(/\s+/g, ' ').trim().replace(/[.。]+$/, '');

const findingVerdict = (finding = {}) =>
  String(finding.verdict || finding.disposition?.verdict || '').toLowerCase();

const hasDisposition = (finding = {}) =>
  Boolean(finding.disposition?.id || finding.disposition_id || finding.verdict || findingVerdict(finding));

const findingImpactAbs = (finding = {}) => Math.abs(num(finding.dollarImpact));

const findingMateriality = (finding = {}) => {
  const impact = findingImpactAbs(finding);
  if (impact) return impact;
  return Math.abs(num(finding.score));
};

const rankFindingsByMateriality = (findings = []) =>
  [...findings].sort((a, b) => findingMateriality(b) - findingMateriality(a));

export const buildSignalMix = (row, findings = [], drivers = []) => {
  const findingAbs = findings.reduce((sum, finding) => sum + findingImpactAbs(finding), 0);
  const driverAbs = drivers.reduce((sum, line) => sum + Math.abs(num(line.variance)), 0);
  const totalAbs = Math.max(Math.abs(num(row?.variance)), findingAbs, driverAbs, 0);
  let anomalous = 0;
  let dispositioned = 0;
  findings.forEach((finding) => {
    const impact = findingImpactAbs(finding);
    if (!impact) return;
    if (hasDisposition(finding)) dispositioned += impact;
    else anomalous += impact;
  });
  const ordinary = Math.max(0, totalAbs - anomalous - dispositioned);
  const denominator = Math.max(totalAbs, anomalous + dispositioned + ordinary, 1);
  const segments = [
    { id: 'anomalous', label: 'Open anomaly', value: anomalous },
    { id: 'dispositioned', label: 'Dispositioned / expected', value: dispositioned },
    { id: 'ordinary', label: 'Ordinary / no signal', value: ordinary }
  ].map((segment) => ({
    ...segment,
    pct: segment.value > 0 ? (segment.value / denominator) * 100 : 0
  }));
  return { totalAbs: denominator, segments };
};

const classifyFinding = (finding = {}) => {
  const verdict = findingVerdict(finding);
  const status = String(finding.status || '').toLowerCase();
  if (hasDisposition(finding)) {
    if (verdict === 'known_recurring') return 'Known recurring';
    if (verdict === 'noise') return 'Noise / suppressed';
    if (verdict === 'real') return 'Confirmed signal';
    return 'Dispositioned';
  }
  if (status === 'suppressed' || status === 'superseded') return 'Suppressed';
  return 'Open anomaly';
};

const isBalanceSheetRow = (row = {}) =>
  (Array.isArray(row.rollups) && row.rollups.includes('Balance Sheet')) || row.screen === 'bs';

// Deterministic fallback narrative in the variance-analysis 4-part structure
// (WHAT / WHY / SO WHAT / NOW WHAT). It quotes only figures that already come from
// the deterministic model (row/driver variances) — never an invented number or
// threshold — and, per the month-end-closer discipline, flags "driver unclear /
// needs controller or analyst review" rather than fabricating a driver.
export const fallbackNarrative = (row, drivers = [], basis, findings = []) => {
  const basisText = basisLabel(basis).toLowerCase();
  const varianceText = formatAccounting(row.variance);
  const pctText = row.variancePct && row.variancePct !== 'NM' ? ` (${row.variancePct})` : '';
  const balanceSheet = isBalanceSheetRow(row);

  // WHAT — exact variance vs the selected basis.
  const what = balanceSheet
    ? `${row.label} ended the period ${varianceText}${pctText} versus ${basisText}.`
    : `${row.label} was ${varianceText}${pctText} versus ${basisText}.`;

  // WHY — 1-3 quantified drivers from the table/waterfall, or an explicit unclear flag.
  const rankedDrivers = (drivers || []).filter((line) => Math.abs(num(line.variance)) > 0);
  const driverText = summarizeDrivers(rankedDrivers);
  let why;
  if (driverText) {
    why = balanceSheet
      ? `Rolling the balance forward, the movement ties to ${driverText}.`
      : `The largest table and waterfall drivers were ${driverText}.`;
  } else {
    why =
      'No deterministic bridge, finding, or comment supports a driver here — flag driver unclear for controller or analyst review before sign-off.';
  }

  // SO WHAT — disposition / expectation / anomaly implication from close-intel evidence.
  const dispositioned = rankFindingsByMateriality(findings.filter(hasDisposition));
  const open = rankFindingsByMateriality(findings.filter((finding) => !hasDisposition(finding)));
  let soWhat = 'Close intelligence did not flag a bottom-up anomaly for this bucket.';
  if (dispositioned.length && open.length) {
    const finding = dispositioned[0];
    const openFinding = open[0];
    const brief = cleanSentence(finding.brief || finding.glGroup || row.label);
    const openBrief = cleanSentence(openFinding.brief || openFinding.glGroup || row.label);
    const verdict = classifyFinding(finding).toLowerCase();
    soWhat =
      `Close intelligence ties the change to ${brief}, dispositioned as ${verdict}, and still carries the largest open anomaly on ${openBrief}.`;
  } else if (dispositioned.length) {
    const finding = dispositioned[0];
    const brief = cleanSentence(finding.brief || finding.glGroup || row.label);
    const verdict = classifyFinding(finding).toLowerCase();
    soWhat = `Close intelligence ties the change to ${brief}, dispositioned as ${verdict}.`;
  } else if (open.length) {
    const finding = open[0];
    const brief = cleanSentence(finding.brief || finding.glGroup || row.label);
    soWhat = `Close intelligence still carries an open anomaly on ${brief}, not yet dispositioned.`;
  }

  // NOW WHAT — action/outlook only from provided dispositions/comments; no invented thresholds.
  const note = cleanSentence(dispositioned.find((finding) => finding.dispositionNote)?.dispositionNote || '');
  let nowWhat;
  if (note && open.length) {
    nowWhat = `Carry the analyst disposition into the review package: ${note}; route the open anomaly before sign-off.`;
  } else if (note) {
    nowWhat = `Carry the analyst disposition into the review package: ${note}.`;
  } else if (open.length) {
    nowWhat = 'Route the open anomaly to an analyst for disposition before the package is signed off.';
  } else {
    nowWhat = 'Confirm in the drilldown whether this is expected, recurring, or still unresolved before controller sign-off.';
  }
  return `${what} ${why} ${soWhat} ${nowWhat}`;
};

export const buildExecutiveSignificantItems = ({
  board = {},
  lines = [],
  summaryRows = buildExecutiveSummaryRows(lines),
  findingsById = {},
  evidence = {},
  basis = 'budget',
  limit = 6
} = {}) => {
  // Executive-row narratives are persisted at the EXACT matrix/waterfall/drilldown row
  // id (scope_key) and surfaced by board_payload as execRows[row.id]. Bind each row to
  // its own narrative — never borrow a constituent planning-entity line's story for an
  // aggregate row, and never fall back to the company narrative for a balance-sheet row.
  const execRows = board?.execRows || board?.exec_rows || {};
  const findingsByResolvedId = new Map();
  normalizeFindings(evidence?.findings).forEach((finding) => {
    if (finding.id) findingsByResolvedId.set(String(finding.id), finding);
  });
  normalizeFindings(findingsById).forEach((finding) => {
    if (finding.id) findingsByResolvedId.set(String(finding.id), finding);
  });
  const findings = Array.from(findingsByResolvedId.values());

  const rows = [...summaryRows]
    .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance))
    .slice(0, limit);

  return rows.map((row) => {
    const rowLines = (lines || []).filter((line) => rowMatchesLine(row, line));
    const drivers = executiveDriverLines(row, lines, 5);
    const matchingFindings = rankFindingsByMateriality(
      findings.filter((finding) => findingMatchesAnyLine(finding, rowLines))
    );
    const rowFindings = matchingFindings.filter((finding, index, arr) =>
      finding.id && arr.findIndex((candidate) => candidate.id === finding.id) === index
    );
    const rowBridgeIds = bridgeIdsForLines(evidence, drivers);

    // Exact executive-row narrative, keyed by row.id (never a constituent line).
    const execNarrative = execRows[row.id] || null;
    const execBody = narrativeBody(execNarrative);
    const execCitations = narrativeCitations(execNarrative);

    const citations = mergeCitationLists(
      execCitations,
      citationObj('findingIds', rowFindings.map((finding) => finding.id)),
      citationObj('dispositionIds', dispositionIdsFromFindings(rowFindings)),
      citationObj('bridgeComponentIds', rowBridgeIds)
    );
    // Prefer the persisted, guarded, cited executive-row narrative; only synthesize the
    // deterministic variance-analysis fallback when the exact exec narrative is absent.
    const body =
      execBody ||
      fallbackNarrative(row, drivers, basis, rowFindings) ||
      rowFindings.find((finding) => finding.brief)?.brief;
    const signalMix = buildSignalMix(row, rowFindings, rowLines);

    return {
      id: row.id,
      label: row.label,
      section: row.section,
      screen: row.screen,
      actual: row.actual,
      reference: row.reference,
      variance: row.variance,
      variancePct: row.variancePct,
      body,
      citations,
      hasExecNarrative: Boolean(execBody),
      guardPass: execNarrative ? (execNarrative.guardPass ?? execNarrative.guard_pass) : undefined,
      drivers: drivers.slice(0, 4).map((line) => ({
        key: lineKey(line),
        planningEntity: line.planningEntity,
        glGroup: line.glGroup,
        variance: num(line.variance),
        hasNarrative: Boolean(line.narrative),
        narrative: narrativeBody(line.narrative),
        citations: narrativeCitations(line.narrative)
      })),
      findings: rowFindings.slice(0, 6).map((finding) => ({
        ...finding,
        classificationLabel: classifyFinding(finding)
      })),
      signalMix
    };
  });
};

export const excerptTwoSentences = (text = '') => {
  const trimmed = String(text || '').trim();
  if (!trimmed) return { excerpt: '', remainder: '', hasMore: false };
  const matches = trimmed.match(/[^.!?]+[.!?]+/g);
  if (!matches || matches.length <= 2) {
    return { excerpt: trimmed, remainder: '', hasMore: false };
  }
  const excerpt = matches.slice(0, 2).join('').trim();
  const remainder = matches.slice(2).join('').trim();
  return { excerpt, remainder, hasMore: Boolean(remainder) };
};

export const narrativeBody = (node) => {
  if (!node) return '';
  if (typeof node === 'string') return node;
  return node.body || node.narrative || node.summary || '';
};

export const narrativeCitations = (node) => {
  if (!node || typeof node === 'string') return {};
  return node.citations || {};
};

export const groupCommentaryByRollup = (lines = []) => {
  const grouped = groupBoardLines(lines);
  return grouped
    .map((group) => ({
      rollup: group.rollup,
      heading: rollupHeadingLabel(group.rollup),
      items: group.rows
        .filter((line) => narrativeBody(line.narrative))
        .map((line) => ({
          key: lineKey(line),
          glGroup: line.glGroup,
          planningEntity: line.planningEntity,
          body: narrativeBody(line.narrative),
          citations: narrativeCitations(line.narrative)
        }))
    }))
    .filter((group) => group.items.length > 0);
};

const componentType = (component) =>
  String(component.type || component.component_type || component.componentType || '').toLowerCase();

const componentAmount = (component) =>
  num(component.amount ?? component.value ?? component.variance ?? component.delta);

const pickCalcInputs = (components = []) => {
  for (const component of components) {
    const inputs = component.calcInputs || component.calc_inputs;
    if (inputs && typeof inputs === 'object') return inputs;
  }
  return null;
};

const hasRateVolumeInputs = (inputs = {}) =>
  [
    'balanceActual',
    'balanceReference',
    'rateActual',
    'rateReference',
    'avgBalance',
    'avgBalanceActual',
    'avgBalanceReference'
  ].some((key) => inputs[key] !== undefined && inputs[key] !== null);

const readBalance = (inputs, kind) => {
  if (kind === 'act') {
    return inputs.balanceActual ?? inputs.avgBalanceActual ?? inputs.avgBalance ?? null;
  }
  return inputs.balanceReference ?? inputs.avgBalanceReference ?? null;
};

const formatSourceChip = (source) => {
  const value = String(source || '').toLowerCase();
  if (value === 'tape') return 'tape';
  if (value === 'gl_implied' || value === 'gl implied') return 'gl implied';
  return source || null;
};

export const buildRateVolumeRows = (lineBridgePairs = []) => {
  const rows = [];
  lineBridgePairs.forEach(({ line, bridge }) => {
    const glGroup = String(line?.glGroup || '');
    if (!/interest/i.test(glGroup)) return;
    const components = Array.isArray(bridge?.components) ? bridge.components : [];
    const rvComponents = components.filter((component) =>
      ['rate', 'volume', 'mix', 'cross'].includes(componentType(component))
    );
    if (!rvComponents.length) return;
    const inputs = pickCalcInputs(rvComponents);
    if (!inputs || !hasRateVolumeInputs(inputs)) return;

    const amountFor = (type) =>
      rvComponents
        .filter((component) => componentType(component) === type)
        .reduce((sum, component) => sum + componentAmount(component), 0);

    const volume = amountFor('volume');
    const rate = amountFor('rate');
    const mix = amountFor('mix');
    const cross = amountFor('cross');
    const total =
      bridge?.total !== undefined
        ? num(bridge.total)
        : volume + rate + mix + cross;

    rows.push({
      key: lineKey(line),
      line: glGroup,
      balanceRef: readBalance(inputs, 'ref'),
      balanceAct: readBalance(inputs, 'act'),
      rateRef: inputs.rateReference ?? null,
      rateAct: inputs.rateActual ?? inputs.deltaRate ?? null,
      volumeDelta: rvComponents.some((c) => componentType(c) === 'volume') ? volume : null,
      rateDelta: rvComponents.some((c) => componentType(c) === 'rate') ? rate : null,
      mixDelta: rvComponents.some((c) => componentType(c) === 'mix') ? mix : null,
      total,
      source: formatSourceChip(inputs.source)
    });
  });
  return rows;
};

export const pickMostMaterialLine = (lines = []) => {
  if (!lines.length) return null;
  return [...lines].sort(
    (a, b) => Math.abs(num(b.variance)) - Math.abs(num(a.variance))
  )[0];
};

export const formatRate = (value) => {
  if (value === null || value === undefined) return null;
  const n = num(value);
  if (Math.abs(n) <= 1) return `${(n * 100).toFixed(2)}%`;
  return `${n.toFixed(2)}%`;
};
